import { useEffect, useState } from "react";
import { Arrow } from "../atoms/Arrow";
import type { Copy, Lang } from "../lib/copy";

type NavProps = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Copy;
  variant: "tech" | "warm";
};

/**
 * Slim top nav: brand logo, language toggle and primary CTA — nothing else.
 *
 * The section-menu links (Produkte / Branchen / Integrationen / Kunden) and
 * the mobile burger/drawer were intentionally dropped — users reach those
 * sections by scrolling. The scroll-state still switches the nav to a
 * backdrop-blurred pill once the user moves past the hero fade.
 */
export function Nav({ lang, setLang, t, variant }: NavProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const logoSrc = variant === "warm" ? "/assets/logo-dark.png" : "/assets/logo-light.png";

  return (
    <header className={"nav " + (scrolled ? "is-scrolled" : "")}>
      <div className="nav-inner">
        <a href="#top" className="brand" aria-label="Stratex-AI">
          <img src={logoSrc} alt="Stratex-AI" />
        </a>

        <div className="nav-right">
          <div className="lang-toggle" role="group" aria-label="Language">
            <button
              aria-pressed={lang === "de"}
              onClick={() => setLang("de")}
              className={lang === "de" ? "on" : ""}
            >
              DE
            </button>
            <span>/</span>
            <button
              aria-pressed={lang === "en"}
              onClick={() => setLang("en")}
              className={lang === "en" ? "on" : ""}
            >
              EN
            </button>
          </div>
          <a href="#contact" className="btn btn-primary nav-cta">
            {t.nav.cta} <Arrow />
          </a>
        </div>
      </div>
    </header>
  );
}
